// ─── Types ────────────────────────────────────────────────────────────────────

export interface MortalityRecord {
  id: string;
  recordNumber: string;
  date: string;
  farm: string;
  house: string;
  flockId: string;
  flockNumber: string;
  flockName: string;
  birdType: string;
  ageDays: number;
  openingCount: number;
  numberDead: number;
  numberCulled: number;
  closingCount: number;
  suspectedCause: string;
  confirmedCause: string | null;
  causeCategory: string;
  disposalMethod: string;
  notes: string;
  recordedBy: string;
  status: 'pending' | 'confirmed' | 'under-investigation';
  dailyMortalityPct: number;
  thresholdBreached: boolean;
}

export interface MortalityTrendPoint {
  date: string;
  disease: number;
  culling: number;
  injury: number;
  unknown: number;
  predation: number;
  total: number;
}

export interface CauseSummary {
  cause: string;
  category: string;
  count: number;
  percentage: number;
  color: string;
}

export interface ThresholdConfig {
  flockId: string;
  flockNumber: string;
  dailyThresholdPct: number;
  weeklyThresholdPct: number;
  cumulativeThresholdPct: number;
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const CAUSE_CATEGORIES = [
  'Disease / Infection',
  'Nutritional Deficiency',
  'Injury / Trauma',
  'Predation',
  'Heat Stress',
  'Culling — Poor Performance',
  'Culling — Disease',
  'Culling — Injury',
  'Unknown',
  'Other',
];

export const SUSPECTED_CAUSES = [
  'Newcastle Disease',
  'Gumboro (IBD)',
  'Infectious Bronchitis',
  'Marek\'s Disease',
  'Coccidiosis',
  'Salmonellosis',
  'Fowl Typhoid',
  'Avian Influenza',
  'Respiratory Infection',
  'Nutritional Deficiency',
  'Heat Stress',
  'Dehydration',
  'Injury / Trauma',
  'Predation',
  'Poor Performance (Culled)',
  'Weak / Runts (Culled)',
  'Disease — Culled',
  'Unknown',
  'Other',
];

export const DISPOSAL_METHODS = [
  'Deep Burial',
  'Incineration',
  'Composting',
  'Rendering',
  'Biogas',
  'Other',
];

export const FARMS = ['Idudi Poultry Farm', 'Kampala Demo Farm'];

export const HOUSES_BY_FARM: Record<string, string[]> = {
  'Idudi Poultry Farm': ['House A1', 'House A2', 'House B1', 'Layer House 01', 'Layer House 02', 'Breeder House 01', 'Free Range Area'],
  'Kampala Demo Farm': ['House C1', 'House D2', 'Layer House 03'],
};

export interface FlockOption {
  id: string;
  flockNumber: string;
  flockName: string;
  farm: string;
  house: string;
  currentQty: number;
  birdType: string;
  ageDays: number;
  initialQty: number;
  totalMortality: number;
}

export const FLOCK_OPTIONS: FlockOption[] = [
  { id: 'flock-002', flockNumber: 'BR-2026-002', flockName: 'Batch A2 — Ross 308', farm: 'Idudi Poultry Farm', house: 'House A2', currentQty: 4614, birdType: 'Broiler', ageDays: 44, initialQty: 4800, totalMortality: 186 },
  { id: 'flock-003', flockNumber: 'BR-2026-003', flockName: 'Batch B1 — Cobb 500', farm: 'Idudi Poultry Farm', house: 'House B1', currentQty: 3432, birdType: 'Broiler', ageDays: 25, initialQty: 3500, totalMortality: 68 },
  { id: 'flock-004', flockNumber: 'LY-2026-001', flockName: 'Layers Batch L1 — Lohmann', farm: 'Idudi Poultry Farm', house: 'Layer House 01', currentQty: 7742, birdType: 'Layer', ageDays: 232, initialQty: 8000, totalMortality: 258 },
  { id: 'flock-005', flockNumber: 'LY-2026-002', flockName: 'Layers Batch L2 — Isa Brown', farm: 'Idudi Poultry Farm', house: 'Layer House 02', currentQty: 6318, birdType: 'Layer', ageDays: 196, initialQty: 6500, totalMortality: 182 },
  { id: 'flock-006', flockNumber: 'LY-2026-003', flockName: 'Layers Batch L3 — Lohmann', farm: 'Kampala Demo Farm', house: 'Layer House 03', currentQty: 3940, birdType: 'Layer', ageDays: 95, initialQty: 4000, totalMortality: 60 },
  { id: 'flock-007', flockNumber: 'BRD-2026-001', flockName: 'Breeders Flock BD1 — Ross', farm: 'Idudi Poultry Farm', house: 'Breeder House 01', currentQty: 2358, birdType: 'Breeder', ageDays: 178, initialQty: 2400, totalMortality: 42 },
  { id: 'flock-008', flockNumber: 'BR-2026-004', flockName: 'Batch C1 — Arbor Acres', farm: 'Kampala Demo Farm', house: 'House C1', currentQty: 5974, birdType: 'Broiler', ageDays: 20, initialQty: 6000, totalMortality: 26 },
  { id: 'flock-009', flockNumber: 'LOCAL-2026-001', flockName: 'Local Kienyeji Batch 01', farm: 'Idudi Poultry Farm', house: 'Free Range Area', currentQty: 762, birdType: 'Local/Indigenous', ageDays: 156, initialQty: 800, totalMortality: 38 },
];

export const THRESHOLD_CONFIGS: ThresholdConfig[] = [
  { flockId: 'flock-002', flockNumber: 'BR-2026-002', dailyThresholdPct: 0.5, weeklyThresholdPct: 2.0, cumulativeThresholdPct: 5.0 },
  { flockId: 'flock-003', flockNumber: 'BR-2026-003', dailyThresholdPct: 0.5, weeklyThresholdPct: 2.0, cumulativeThresholdPct: 5.0 },
  { flockId: 'flock-004', flockNumber: 'LY-2026-001', dailyThresholdPct: 0.1, weeklyThresholdPct: 0.5, cumulativeThresholdPct: 4.0 },
  { flockId: 'flock-005', flockNumber: 'LY-2026-002', dailyThresholdPct: 0.1, weeklyThresholdPct: 0.5, cumulativeThresholdPct: 4.0 },
  { flockId: 'flock-006', flockNumber: 'LY-2026-003', dailyThresholdPct: 0.1, weeklyThresholdPct: 0.5, cumulativeThresholdPct: 4.0 },
  { flockId: 'flock-007', flockNumber: 'BRD-2026-001', dailyThresholdPct: 0.1, weeklyThresholdPct: 0.4, cumulativeThresholdPct: 3.0 },
  { flockId: 'flock-008', flockNumber: 'BR-2026-004', dailyThresholdPct: 0.5, weeklyThresholdPct: 2.0, cumulativeThresholdPct: 5.0 },
  { flockId: 'flock-009', flockNumber: 'LOCAL-2026-001', dailyThresholdPct: 1.0, weeklyThresholdPct: 3.0, cumulativeThresholdPct: 8.0 },
];

// ─── Mock Records ─────────────────────────────────────────────────────────────

export const MOCK_MORTALITY_RECORDS: MortalityRecord[] = [
  {
    id: 'mort-001',
    recordNumber: 'MC-2026-089',
    date: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    ageDays: 44,
    openingCount: 4617,
    numberDead: 3,
    numberCulled: 1,
    closingCount: 4613,
    suspectedCause: 'Respiratory Infection',
    confirmedCause: 'Infectious Bronchitis',
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Deep Burial',
    notes: 'Three birds found dead in morning check. One bird culled due to severe respiratory distress.',
    recordedBy: 'James Okello',
    status: 'confirmed',
    dailyMortalityPct: 0.065,
    thresholdBreached: false,
  },
  {
    id: 'mort-002',
    recordNumber: 'MC-2026-088',
    date: '2026-09-04',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    ageDays: 25,
    openingCount: 3435,
    numberDead: 18,
    numberCulled: 4,
    closingCount: 3413,
    suspectedCause: 'Gumboro (IBD)',
    confirmedCause: null,
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Incineration',
    notes: 'Sudden spike in mortality. Bloody droppings observed. Vet notified. Samples sent for lab confirmation.',
    recordedBy: 'Ogenga Paul',
    status: 'under-investigation',
    dailyMortalityPct: 0.524,
    thresholdBreached: true,
  },
  {
    id: 'mort-003',
    recordNumber: 'MC-2026-087',
    date: '2026-09-03',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    birdType: 'Layer',
    ageDays: 231,
    openingCount: 7744,
    numberDead: 2,
    numberCulled: 0,
    closingCount: 7742,
    suspectedCause: 'Unknown',
    confirmedCause: null,
    causeCategory: 'Unknown',
    disposalMethod: 'Deep Burial',
    notes: 'Two birds found dead. No obvious signs of disease. Normal mortality for flock age.',
    recordedBy: 'Sarah Namukasa',
    status: 'pending',
    dailyMortalityPct: 0.026,
    thresholdBreached: false,
  },
  {
    id: 'mort-004',
    recordNumber: 'MC-2026-086',
    date: '2026-09-03',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    ageDays: 24,
    openingCount: 3450,
    numberDead: 12,
    numberCulled: 3,
    closingCount: 3435,
    suspectedCause: 'Gumboro (IBD)',
    confirmedCause: null,
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Incineration',
    notes: 'Continued elevated mortality. Ruffled feathers, lethargy observed. Strict biosecurity enforced.',
    recordedBy: 'Ogenga Paul',
    status: 'under-investigation',
    dailyMortalityPct: 0.348,
    thresholdBreached: false,
  },
  {
    id: 'mort-005',
    recordNumber: 'MC-2026-085',
    date: '2026-09-02',
    farm: 'Idudi Poultry Farm',
    house: 'House B1',
    flockId: 'flock-003',
    flockNumber: 'BR-2026-003',
    flockName: 'Batch B1 — Cobb 500',
    birdType: 'Broiler',
    ageDays: 23,
    openingCount: 3460,
    numberDead: 8,
    numberCulled: 2,
    closingCount: 3450,
    suspectedCause: 'Gumboro (IBD)',
    confirmedCause: null,
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Incineration',
    notes: 'First signs of elevated mortality. Vet called for assessment.',
    recordedBy: 'Ogenga Paul',
    status: 'under-investigation',
    dailyMortalityPct: 0.231,
    thresholdBreached: false,
  },
  {
    id: 'mort-006',
    recordNumber: 'MC-2026-084',
    date: '2026-09-02',
    farm: 'Kampala Demo Farm',
    house: 'House C1',
    flockId: 'flock-008',
    flockNumber: 'BR-2026-004',
    flockName: 'Batch C1 — Arbor Acres',
    birdType: 'Broiler',
    ageDays: 18,
    openingCount: 5976,
    numberDead: 2,
    numberCulled: 0,
    closingCount: 5974,
    suspectedCause: 'Unknown',
    confirmedCause: null,
    causeCategory: 'Unknown',
    disposalMethod: 'Deep Burial',
    notes: 'Normal early-cycle mortality. No disease signs observed.',
    recordedBy: 'Peter Mugisha',
    status: 'confirmed',
    dailyMortalityPct: 0.033,
    thresholdBreached: false,
  },
  {
    id: 'mort-007',
    recordNumber: 'MC-2026-083',
    date: '2026-09-01',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 02',
    flockId: 'flock-005',
    flockNumber: 'LY-2026-002',
    flockName: 'Layers Batch L2 — Isa Brown',
    birdType: 'Layer',
    ageDays: 193,
    openingCount: 6320,
    numberDead: 1,
    numberCulled: 1,
    closingCount: 6318,
    suspectedCause: 'Injury / Trauma',
    confirmedCause: 'Injury / Trauma',
    causeCategory: 'Injury / Trauma',
    disposalMethod: 'Deep Burial',
    notes: 'One bird found dead — suspected pecking injury. One bird culled due to prolapsed vent.',
    recordedBy: 'Apio Christine',
    status: 'confirmed',
    dailyMortalityPct: 0.016,
    thresholdBreached: false,
  },
  {
    id: 'mort-008',
    recordNumber: 'MC-2026-082',
    date: '2026-08-31',
    farm: 'Idudi Poultry Farm',
    house: 'Breeder House 01',
    flockId: 'flock-007',
    flockNumber: 'BRD-2026-001',
    flockName: 'Breeders Flock BD1 — Ross',
    birdType: 'Breeder',
    ageDays: 174,
    openingCount: 2360,
    numberDead: 1,
    numberCulled: 1,
    closingCount: 2358,
    suspectedCause: 'Marek\'s Disease',
    confirmedCause: 'Marek\'s Disease',
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Incineration',
    notes: 'One bird found dead with neurological signs. One bird culled — severe lameness.',
    recordedBy: 'Apio Christine',
    status: 'confirmed',
    dailyMortalityPct: 0.042,
    thresholdBreached: false,
  },
  {
    id: 'mort-009',
    recordNumber: 'MC-2026-081',
    date: '2026-08-30',
    farm: 'Idudi Poultry Farm',
    house: 'Free Range Area',
    flockId: 'flock-009',
    flockNumber: 'LOCAL-2026-001',
    flockName: 'Local Kienyeji Batch 01',
    birdType: 'Local/Indigenous',
    ageDays: 152,
    openingCount: 764,
    numberDead: 2,
    numberCulled: 0,
    closingCount: 762,
    suspectedCause: 'Predation',
    confirmedCause: 'Predation',
    causeCategory: 'Predation',
    disposalMethod: 'Deep Burial',
    notes: 'Two birds missing — feathers found near perimeter fence. Predation by mongoose suspected.',
    recordedBy: 'James Okello',
    status: 'confirmed',
    dailyMortalityPct: 0.262,
    thresholdBreached: false,
  },
  {
    id: 'mort-010',
    recordNumber: 'MC-2026-080',
    date: '2026-08-29',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    ageDays: 38,
    openingCount: 4622,
    numberDead: 5,
    numberCulled: 0,
    closingCount: 4617,
    suspectedCause: 'Respiratory Infection',
    confirmedCause: 'Infectious Bronchitis',
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Deep Burial',
    notes: 'Elevated mortality linked to IB outbreak. Treatment ongoing.',
    recordedBy: 'James Okello',
    status: 'confirmed',
    dailyMortalityPct: 0.108,
    thresholdBreached: false,
  },
  {
    id: 'mort-011',
    recordNumber: 'MC-2026-079',
    date: '2026-08-28',
    farm: 'Idudi Poultry Farm',
    house: 'Layer House 01',
    flockId: 'flock-004',
    flockNumber: 'LY-2026-001',
    flockName: 'Layers Batch L1 — Lohmann',
    birdType: 'Layer',
    ageDays: 225,
    openingCount: 7748,
    numberDead: 3,
    numberCulled: 1,
    closingCount: 7744,
    suspectedCause: 'Coccidiosis',
    confirmedCause: 'Coccidiosis',
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Deep Burial',
    notes: 'Watery droppings observed. Coccidiosis confirmed. Treatment started.',
    recordedBy: 'Sarah Namukasa',
    status: 'confirmed',
    dailyMortalityPct: 0.039,
    thresholdBreached: false,
  },
  {
    id: 'mort-012',
    recordNumber: 'MC-2026-078',
    date: '2026-08-27',
    farm: 'Idudi Poultry Farm',
    house: 'House A2',
    flockId: 'flock-002',
    flockNumber: 'BR-2026-002',
    flockName: 'Batch A2 — Ross 308',
    birdType: 'Broiler',
    ageDays: 36,
    openingCount: 4630,
    numberDead: 8,
    numberCulled: 0,
    closingCount: 4622,
    suspectedCause: 'Respiratory Infection',
    confirmedCause: 'Infectious Bronchitis',
    causeCategory: 'Disease / Infection',
    disposalMethod: 'Deep Burial',
    notes: 'Peak of IB outbreak. Sneezing and nasal discharge widespread.',
    recordedBy: 'James Okello',
    status: 'confirmed',
    dailyMortalityPct: 0.173,
    thresholdBreached: false,
  },
];

// ─── Trend Data (last 14 days) ────────────────────────────────────────────────

export const MORTALITY_TREND_DATA: MortalityTrendPoint[] = [
  { date: '22 Aug', disease: 4, culling: 2, injury: 1, unknown: 1, predation: 0, total: 8 },
  { date: '23 Aug', disease: 3, culling: 1, injury: 0, unknown: 2, predation: 0, total: 6 },
  { date: '24 Aug', disease: 5, culling: 2, injury: 1, unknown: 1, predation: 1, total: 10 },
  { date: '25 Aug', disease: 6, culling: 3, injury: 0, unknown: 2, predation: 0, total: 11 },
  { date: '26 Aug', disease: 8, culling: 2, injury: 2, unknown: 1, predation: 0, total: 13 },
  { date: '27 Aug', disease: 12, culling: 1, injury: 0, unknown: 2, predation: 0, total: 15 },
  { date: '28 Aug', disease: 14, culling: 3, injury: 1, unknown: 1, predation: 0, total: 19 },
  { date: '29 Aug', disease: 9, culling: 2, injury: 0, unknown: 2, predation: 0, total: 13 },
  { date: '30 Aug', disease: 5, culling: 1, injury: 0, unknown: 1, predation: 2, total: 9 },
  { date: '31 Aug', disease: 4, culling: 2, injury: 1, unknown: 1, predation: 0, total: 8 },
  { date: '01 Sep', disease: 3, culling: 2, injury: 1, unknown: 1, predation: 0, total: 7 },
  { date: '02 Sep', disease: 10, culling: 2, injury: 0, unknown: 2, predation: 0, total: 14 },
  { date: '03 Sep', disease: 15, culling: 3, injury: 0, unknown: 2, predation: 0, total: 20 },
  { date: '04 Sep', disease: 21, culling: 5, injury: 1, unknown: 2, predation: 0, total: 29 },
];

export const CAUSE_SUMMARY_DATA: CauseSummary[] = [
  { cause: 'Disease / Infection', category: 'disease', count: 142, percentage: 58.2, color: '#dc2626' },
  { cause: 'Culling (Various)', category: 'culling', count: 48, percentage: 19.7, color: '#f59e0b' },
  { cause: 'Unknown', category: 'unknown', count: 28, percentage: 11.5, color: '#6b7280' },
  { cause: 'Injury / Trauma', category: 'injury', count: 14, percentage: 5.7, color: '#f97316' },
  { cause: 'Predation', category: 'predation', count: 8, percentage: 3.3, color: '#8b5cf6' },
  { cause: 'Heat Stress', category: 'other', count: 4, percentage: 1.6, color: '#06b6d4' },
];
